/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file notificationLaunchIntent.js
 * @description
 * A dependency-free record of "the current launch / foreground was driven by a
 * notification tap", published the instant that tap is observed and read by the
 * boot surfaces that would otherwise stand between the user and the screen they
 * actually asked for.
 *
 * WHY THIS EXISTS
 * A cold, push-driven launch used to run the FULL branding boot before anything
 * mail-related could happen:
 *   1. `App.jsx` shows `LoadingFallback` until `loadRuntimeState()` settles,
 *   2. `SplashScreenWrapper` then plays the Lottie splash — up to
 *      `SPLASH_TIMEOUT_MS` (3.5 s) plus a 0.5 s logo fade plus a 1 s hold, so
 *      1.5-5 s of pure branding,
 *   3. only THEN does `AppNavigator` mount and `navigationRef` become ready, at
 *      which point `navigateToNotificationTarget`'s retry loop finally lands.
 * The mail fast path (see `helpers/mailNotifications` + `helpers/mailCache`)
 * already has the message cached long before step 3 — the wait was the boot
 * chrome, not the data. Tapping a mail banner is an explicit request for one
 * specific screen, so the branding animation is exactly the wrong thing to show.
 *
 * DESIGN
 * Deliberately imports NOTHING. It is read from `SplashScreenWrapper`, which
 * renders OUTSIDE `AppProviders` (and therefore outside the app's ErrorBoundary,
 * see [[services-app-splash-outside-errorboundary]]) — pulling the notification
 * stack (expo-notifications / expo-task-manager / firebase) into that module
 * graph would both slow the first frame and re-open that boot-crash hazard.
 *
 * The intent is published through a tiny synchronous pub/sub because the
 * cold-start tap is recovered via an ASYNC native call
 * (`Notifications.getLastNotificationResponseAsync()`), so it regularly lands a
 * few hundred milliseconds AFTER React's first render — i.e. while the splash is
 * already on screen. Subscribers cut it short instead of running out the clock.
 *
 * Intents expire ({@link NOTIFICATION_LAUNCH_INTENT_TTL_MS}) so a long-lived JS
 * runtime — one iOS launched headlessly for a background fetch hours ago — can
 * never keep skipping the splash for launches that had nothing to do with a tap.
 */

/**
 * How long a recorded tap keeps counting as "this launch is notification-driven".
 *
 * Sized to comfortably cover the worst-case boot window it has to survive
 * (`BOOT_MAX_MS` = 8 s in App.jsx, plus the splash and the navigator mount)
 * while still being far shorter than any plausible gap between an old tap and an
 * unrelated, user-initiated launch of an already-running JS runtime.
 *
 * @type {number}
 */
export const NOTIFICATION_LAUNCH_INTENT_TTL_MS = 30 * 1000;

/** @type {{at: number, source: string, target: string}|null} */
let currentIntent = null;

/** @type {Set<(intent: {at: number, source: string, target: string}) => void>} */
const listeners = new Set();

/**
 * The active intent, or `null` when there is none or it has aged out.
 *
 * Expiry is evaluated lazily on read (and the stale value dropped) so no timer
 * has to be kept alive for it.
 *
 * @returns {{at: number, source: string, target: string}|null} The fresh intent, or null.
 */
export const getNotificationLaunchIntent = () => {
    if (!currentIntent) {
        return null;
    }

    if (Date.now() - currentIntent.at > NOTIFICATION_LAUNCH_INTENT_TTL_MS) {
        currentIntent = null;

        return null;
    }

    return currentIntent;
};

/**
 * Whether the current launch / foreground was driven by a notification tap.
 *
 * @returns {boolean} true while a fresh intent is recorded.
 */
export const hasNotificationLaunchIntent = () => Boolean(getNotificationLaunchIntent());

/**
 * Record that the user tapped a notification, and tell every subscriber.
 *
 * Never throws: a listener that blows up must not be able to break the tap
 * handling (which is what actually opens the mail) — it is swallowed per
 * listener so the remaining subscribers still run.
 *
 * @param {{source?: string, target?: string}} [details] Diagnostics — where the tap
 *   was observed (`'notification-tap'`, `'fcm-tap'`, …) and the resolved screen.
 * @returns {{at: number, source: string, target: string}} The recorded intent.
 */
export const markNotificationLaunchIntent = (details = {}) => {
    currentIntent = {
        at: Date.now(),
        source: String(details.source || 'unknown'),
        target: String(details.target || ''),
    };

    for (const listener of Array.from(listeners)) {
        try {
            listener(currentIntent);
        } catch (_e) {
            // A broken subscriber must never break notification handling.
        }
    }

    return currentIntent;
};

/**
 * Subscribe to notification-driven launches.
 *
 * If an intent is ALREADY recorded the listener fires synchronously, once,
 * before this function returns — so a subscriber that mounts after the tap was
 * observed (the normal ordering: the tap responder is installed at module scope,
 * React mounts later) can never miss it and does not need its own initial read.
 *
 * @param {(intent: {at: number, source: string, target: string}) => void} listener Called on every mark.
 * @returns {() => void} Unsubscribe.
 */
export const subscribeToNotificationLaunchIntent = (listener) => {
    if (typeof listener !== 'function') {
        return () => {};
    }

    listeners.add(listener);

    const existing = getNotificationLaunchIntent();
    if (existing) {
        try {
            listener(existing);
        } catch (_e) {
            // See markNotificationLaunchIntent — never propagate subscriber errors.
        }
    }

    return () => {
        listeners.delete(listener);
    };
};

/**
 * Drop the recorded intent. Used by tests; also safe to call from app code when
 * a surface wants to make sure a handled tap cannot influence a later boot.
 *
 * @returns {void}
 */
export const clearNotificationLaunchIntent = () => {
    currentIntent = null;
};
